import { uniqueBy } from '@helpers/utils/arrays/arrays';
import {
  getTickerKey,
  PRICE_WS_NORMAL_CLOSURE,
  PriceWsCloseReason,
  TickerPayload,
} from '@helpers/utils/price-ws/price-ws';
import {
  createPriceWS,
  PriceWSInstance,
} from '@terminal/services/price-ws/price-ws-service';
import { WebSocketStatus } from '@terminal/services/use-base-ws/utils';
import { create } from '@terminal/utils/store/createStore';

import { isTickerInList, SubscribeData, WsInitConfig } from './utils';

interface LivePriceWebSocketStore {
  initialize: (config: WsInitConfig) => PriceWSInstance;
  getPriceWS: (config: WsInitConfig) => PriceWSInstance;
  subscribe: (data: SubscribeData) => void;
  unsubscribe: (tickers: TickerPayload[]) => void;
  closeConnection: () => void;
  priceWS: PriceWSInstance | null;
  subscribedTickers: TickerPayload[];
}

export const useLivePriceWebSocketStore = create<LivePriceWebSocketStore>(
  (set, get) => ({
    priceWS: null,
    subscribedTickers: [],
    initialize: ({ updateHandler, delayedPrices }) => {
      const priceWS = createPriceWS({
        updateHandler,
        delayedPrices,
        getSubscribedTickers: () => get().subscribedTickers,
      });
      set({ priceWS });

      return priceWS;
    },

    getPriceWS: ({ updateHandler, delayedPrices }) => {
      const { priceWS, initialize } = get();
      const isWebSocketClosed =
        !priceWS ||
        priceWS.status === WebSocketStatus.CLOSED ||
        priceWS.status === WebSocketStatus.UNINSTANTIATED;

      return isWebSocketClosed
        ? initialize({ updateHandler, delayedPrices })
        : priceWS;
    },

    subscribe: ({ tickers, updateHandler, delayedPrices }) => {
      const { getPriceWS, subscribedTickers } = get();
      const uniqueIncoming = uniqueBy(tickers, getTickerKey);
      const newTickers = uniqueIncoming.filter(
        ticker => !isTickerInList(ticker, subscribedTickers)
      );

      set({
        subscribedTickers: uniqueBy(
          [...subscribedTickers, ...newTickers],
          getTickerKey
        ),
      });

      const priceWS = getPriceWS({ updateHandler, delayedPrices });
      priceWS.subscribe(newTickers);
    },

    unsubscribe: (tickers: TickerPayload[]) => {
      const { priceWS, subscribedTickers } = get();

      const nextSubscribed = subscribedTickers.filter(
        t => !isTickerInList(t, tickers)
      );
      set({ subscribedTickers: nextSubscribed });

      priceWS?.unsubscribe(tickers);
    },

    closeConnection: () => {
      const { priceWS } = get();

      priceWS?.disconnect(
        PRICE_WS_NORMAL_CLOSURE,
        PriceWsCloseReason.NoConnectionRequired
      );
      set({ priceWS: null, subscribedTickers: [] });
    },
  })
);
